import type { MediaServiceError } from "@/server/services/media.service"

/** Safe, stable codes returned from upload routes (never provider-specific). */
export const UPLOAD_PUBLIC_CODES = {
  UPLOAD_FAILED_GENERIC: "UPLOAD_FAILED_GENERIC",
  UPLOAD_TEMPORARILY_UNAVAILABLE: "UPLOAD_TEMPORARILY_UNAVAILABLE",
  FILE_TOO_LARGE: "FILE_TOO_LARGE",
  UNSUPPORTED_FILE_TYPE: "UNSUPPORTED_FILE_TYPE",
  NOT_ALLOWED: "NOT_ALLOWED",
  /** Event album photo count limit (active rows). */
  EVENT_MEDIA_PHOTO_LIMIT: "EVENT_MEDIA_PHOTO_LIMIT",
  /** Event album video count limit (active rows). */
  EVENT_MEDIA_VIDEO_LIMIT: "EVENT_MEDIA_VIDEO_LIMIT",
  /** Per-video duration cap for event uploads. */
  EVENT_MEDIA_VIDEO_DURATION: "EVENT_MEDIA_VIDEO_DURATION",
} as const

export type UploadPublicCode =
  (typeof UPLOAD_PUBLIC_CODES)[keyof typeof UPLOAD_PUBLIC_CODES]

export const UPLOAD_PUBLIC_CODE_SET = new Set<string>(
  Object.values(UPLOAD_PUBLIC_CODES),
)

export function isUploadPublicCode(c: string): c is UploadPublicCode {
  return UPLOAD_PUBLIC_CODE_SET.has(c)
}

/** Classifier set on {@link MediaServiceError} at throw sites (server-only). */
export type MediaUploadFailureClass =
  | "upstream_not_found"
  | "upstream_retryable"
  | "upstream_client_error"
  | "payload_too_large"
  | "unsupported_type"
  | "permission_denied"
  | "missing_dependency"
  | "misconfiguration"
  | "invalid_upstream_response"

const GENERIC_MESSAGE =
  "Upload couldn't be completed. Please try again."
const TEMP_UNAVAILABLE_MESSAGE =
  "Upload is temporarily unavailable. Please try again in a moment."
const FILE_TOO_LARGE_MESSAGE =
  "This file is too large. Try a smaller file."
const UNSUPPORTED_TYPE_MESSAGE =
  "This file type isn't supported. Try JPEG, PNG, or WebP."
const NOT_ALLOWED_MESSAGE =
  "This upload isn't available on your plan or for this event."
const EVENT_MEDIA_PHOTO_LIMIT_MESSAGE =
  "This event can hold up to 200 photos."
const EVENT_MEDIA_VIDEO_LIMIT_MESSAGE =
  "This event can hold up to 5 videos."
const EVENT_MEDIA_VIDEO_DURATION_MESSAGE =
  "Videos can be up to 60 seconds long."

/** Short safe copy included in JSON (matches user-facing intent). */
export function publicApiMessageForUploadCode(code: UploadPublicCode): string {
  switch (code) {
    case UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC:
      return GENERIC_MESSAGE
    case UPLOAD_PUBLIC_CODES.UPLOAD_TEMPORARILY_UNAVAILABLE:
      return TEMP_UNAVAILABLE_MESSAGE
    case UPLOAD_PUBLIC_CODES.FILE_TOO_LARGE:
      return FILE_TOO_LARGE_MESSAGE
    case UPLOAD_PUBLIC_CODES.UNSUPPORTED_FILE_TYPE:
      return UNSUPPORTED_TYPE_MESSAGE
    case UPLOAD_PUBLIC_CODES.NOT_ALLOWED:
      return NOT_ALLOWED_MESSAGE
    case UPLOAD_PUBLIC_CODES.EVENT_MEDIA_PHOTO_LIMIT:
      return EVENT_MEDIA_PHOTO_LIMIT_MESSAGE
    case UPLOAD_PUBLIC_CODES.EVENT_MEDIA_VIDEO_LIMIT:
      return EVENT_MEDIA_VIDEO_LIMIT_MESSAGE
    case UPLOAD_PUBLIC_CODES.EVENT_MEDIA_VIDEO_DURATION:
      return EVENT_MEDIA_VIDEO_DURATION_MESSAGE
    default:
      return GENERIC_MESSAGE
  }
}

export type ResolvedPublicUploadFailure = {
  code: UploadPublicCode
  message: string
  httpStatus: number
}

function fromClass(
  c: MediaUploadFailureClass,
): Omit<ResolvedPublicUploadFailure, "message"> & { message?: string } {
  switch (c) {
    case "upstream_not_found":
    case "misconfiguration":
    case "invalid_upstream_response":
      return {
        code: UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC,
        httpStatus: 502,
      }
    case "upstream_retryable":
    case "missing_dependency":
      return {
        code: UPLOAD_PUBLIC_CODES.UPLOAD_TEMPORARILY_UNAVAILABLE,
        httpStatus: 503,
      }
    case "upstream_client_error":
      return {
        code: UPLOAD_PUBLIC_CODES.UNSUPPORTED_FILE_TYPE,
        httpStatus: 400,
      }
    case "payload_too_large":
      return {
        code: UPLOAD_PUBLIC_CODES.FILE_TOO_LARGE,
        httpStatus: 413,
      }
    case "unsupported_type":
      return {
        code: UPLOAD_PUBLIC_CODES.UNSUPPORTED_FILE_TYPE,
        httpStatus: 400,
      }
    case "permission_denied":
      return {
        code: UPLOAD_PUBLIC_CODES.NOT_ALLOWED,
        httpStatus: 403,
      }
    default: {
      const _exhaustive: never = c
      return _exhaustive
    }
  }
}

/**
 * Maps a media-layer error to a safe HTTP envelope (no provider or env details).
 */
export function resolvePublicUploadFailure(
  e: MediaServiceError,
): ResolvedPublicUploadFailure {
  if (e.uploadFailureClass) {
    const r = fromClass(e.uploadFailureClass)
    return {
      code: r.code,
      message: publicApiMessageForUploadCode(r.code),
      httpStatus: r.httpStatus,
    }
  }

  const trimmed = e.clientFacingMessage?.trim()
  if (trimmed && e.clientCode && isUploadPublicCode(e.clientCode)) {
    return {
      code: e.clientCode,
      message: trimmed,
      httpStatus:
        e.statusCode >= 400 && e.statusCode < 600 ? e.statusCode : 400,
    }
  }

  if (e.statusCode === 413) {
    return {
      code: UPLOAD_PUBLIC_CODES.FILE_TOO_LARGE,
      message: publicApiMessageForUploadCode(
        UPLOAD_PUBLIC_CODES.FILE_TOO_LARGE,
      ),
      httpStatus: 413,
    }
  }
  if (e.statusCode === 403) {
    return {
      code: UPLOAD_PUBLIC_CODES.NOT_ALLOWED,
      message: publicApiMessageForUploadCode(UPLOAD_PUBLIC_CODES.NOT_ALLOWED),
      httpStatus: 403,
    }
  }
  if (e.statusCode === 401) {
    return {
      code: UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC,
      message: publicApiMessageForUploadCode(
        UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC,
      ),
      httpStatus: 401,
    }
  }
  if (e.statusCode === 503) {
    return {
      code: UPLOAD_PUBLIC_CODES.UPLOAD_TEMPORARILY_UNAVAILABLE,
      message: publicApiMessageForUploadCode(
        UPLOAD_PUBLIC_CODES.UPLOAD_TEMPORARILY_UNAVAILABLE,
      ),
      httpStatus: 503,
    }
  }
  if (e.statusCode === 404) {
    return {
      code: UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC,
      message: publicApiMessageForUploadCode(
        UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC,
      ),
      httpStatus: 404,
    }
  }
  if (e.statusCode === 400) {
    return {
      code: UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC,
      message: publicApiMessageForUploadCode(
        UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC,
      ),
      httpStatus: 400,
    }
  }

  return {
    code: UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC,
    message: publicApiMessageForUploadCode(
      UPLOAD_PUBLIC_CODES.UPLOAD_FAILED_GENERIC,
    ),
    httpStatus: e.statusCode >= 400 && e.statusCode < 600 ? e.statusCode : 502,
  }
}
